import React, { useState, useEffect } from 'react';
import { useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import env from './utils/env';

interface User {
    _id: string;
    username: string;
    email: string;
    role: 'ADMIN' | 'USER' | 'FREE' | 'TRIAL';
    isActive: boolean;
    emailVerified: boolean;
    createdAt: string;
    lastLogin?: string;
}

interface UserListResponse {
    users: User[];
    pagination: {
        totalDocuments: number;
        totalPages: number;
        currentPage: number;
        pageSize: number;
        hasNextPage: boolean;
        hasPreviousPage: boolean;
    };
}

interface ActionMessage {
    type: 'success' | 'error' | 'info';
    text: string;
}

const UserManagementView: React.FC = () => {
    const { token } = useAuth();
    const { t } = useTranslation();
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [actionMessage, setActionMessage] = useState<ActionMessage | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalUsers, setTotalUsers] = useState(0);
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
    const [processingActions, setProcessingActions] = useState<Set<string>>(new Set());
    const [showActionsHelp, setShowActionsHelp] = useState(false);

    const apiBaseUrl = env.apiUrl;

    const fetchUsers = async (page: number = 1) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${apiBaseUrl}/admin/users?page=${page}&limit=20`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch users: ${response.statusText}`);
            }

            const data: UserListResponse = await response.json();
            setUsers(data.users);
            setCurrentPage(data.pagination.currentPage);
            setTotalPages(data.pagination.totalPages);
            setTotalUsers(data.pagination.totalDocuments);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers(currentPage);
    }, [currentPage, token]);

    const showActionMessage = (type: ActionMessage['type'], text: string) => {
        setActionMessage({ type, text });
        setTimeout(() => setActionMessage(null), 5000);
    };

    const deleteUser = async (userId: string) => {
        setProcessingActions(prev => new Set(prev).add(userId));
        try {
            const response = await fetch(`${apiBaseUrl}/admin/users/${userId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to delete user');
            }

            showActionMessage('success', t('userManagement.messages.userDeleted'));
            await fetchUsers(currentPage);
        } catch (err: any) {
            showActionMessage('error', err.message);
        } finally {
            setProcessingActions(prev => {
                const newSet = new Set(prev);
                newSet.delete(userId);
                return newSet;
            });
            setDeleteConfirm(null);
        }
    };



    const changeUserRole = async (userId: string, newRole: string) => {
        setProcessingActions(prev => new Set(prev).add(userId));
        try {
            const response = await fetch(`${apiBaseUrl}/admin/users/${userId}/role`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ role: newRole }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to change user role');
            }

            showActionMessage('success', t('userManagement.messages.roleChanged', { role: newRole }));
            await fetchUsers(currentPage);
        } catch (err: any) {
            showActionMessage('error', err.message);
        } finally {
            setProcessingActions(prev => {
                const newSet = new Set(prev);
                newSet.delete(userId);
                return newSet;
            });
        }
    };

    const resendVerification = async (userId: string) => {
        setProcessingActions(prev => new Set(prev).add(userId));
        try {
            const response = await fetch(`${apiBaseUrl}/admin/users/${userId}/resend-verification`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to resend verification email');
            }

            showActionMessage('success', t('userManagement.messages.verificationResent'));
        } catch (err: any) {
            showActionMessage('error', err.message);
        } finally {
            setProcessingActions(prev => {
                const newSet = new Set(prev);
                newSet.delete(userId);
                return newSet;
            });
        }
    };

    const getRoleBadgeClass = (role: string) => {
        switch (role) {
            case 'ADMIN':
                return 'role-badge admin';
            case 'USER':
                return 'role-badge user';
            case 'FREE':
                return 'role-badge free';
            case 'TRIAL':
                return 'role-badge trial';
            default:
                return 'role-badge';
        }
    };

    const getStatusBadgeClass = (user: User) => {
        if (!user.isActive) return 'status-badge inactive';
        if (!user.emailVerified) return 'status-badge unverified';
        return 'status-badge active';
    };

    const getStatusText = (user: User) => {
        if (!user.isActive) return 'Inactive';
        if (!user.emailVerified) return 'Unverified';
        return 'Active';
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading && users.length === 0) {
        return (
            <div className="container">
                <div className="loading">{t('loading.users')}</div>
            </div>
        );
    }

    return (
        <div className="container">
            <div className="user-management-header">
                <h1>{t('userManagement.title')}</h1>
                <div className="user-stats">
                    <span className="stat">{t('userManagement.totalUsers', { count: totalUsers })}</span>
                    <span className="stat">{t('pagination.page')} {currentPage} {t('pagination.of')} {totalPages}</span>
                </div>
            </div>

            {actionMessage && (
                <div className={`action-message ${actionMessage.type}`}>
                    {actionMessage.text}
                </div>
            )}

            {error && (
                <div className="error-message">
                    {t('errors.error')} {error}
                    <button onClick={() => fetchUsers(currentPage)} className="retry-button">
                        {t('buttons.retry')}
                    </button>
                </div>
            )}

            <div className="users-table-container">
                <table className="users-table">
                    <thead>
                        <tr>
                            <th>{t('userManagement.headers.user')}</th>
                            <th>{t('userManagement.headers.role')}</th>
                            <th>{t('userManagement.headers.status')}</th>
                            <th>{t('userManagement.headers.created')}</th>
                            <th>{t('userManagement.headers.lastLogin')}</th>
                            <th>
                                <div className="actions-header">
                                    <span>{t('userManagement.headers.actions')}</span>
                                    <button
                                        type="button"
                                        className="actions-header-help"
                                        onClick={() => setShowActionsHelp(true)}
                                        title={t('userManagement.actionsHelp.title')}
                                    >
                                        <span className="material-icons">help</span>
                                    </button>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((user) => (
                            <tr key={user._id}>
                                <td className="user-info-cell">
                                    <div className="user-details">
                                        <div className="username">{user.username}</div>
                                        <div className="email">{user.email}</div>
                                    </div>
                                </td>
                                <td>
                                    <span className={getRoleBadgeClass(user.role)}>
                                        {user.role === 'USER' ? t('user.roles.pro') : user.role}
                                    </span>
                                </td>
                                <td>
                                    <span className={getStatusBadgeClass(user)}>
                                        {getStatusText(user)}
                                    </span>
                                </td>
                                <td className="date-cell">
                                    {formatDate(user.createdAt)}
                                </td>
                                <td className="date-cell">
                                    {user.lastLogin ? formatDate(user.lastLogin) : t('userManagement.never')}
                                </td>
                                <td className="actions-cell">
                                    <div className="action-buttons">
                                        {/* Role management buttons */}
                                        {user.role === 'FREE' && (
                                            <>
                                                <button
                                                    className="action-btn-labeled upgrade-btn"
                                                    onClick={() => changeUserRole(user._id, 'TRIAL')}
                                                    disabled={processingActions.has(user._id)}
                                                    title="Upgrade to Trial"
                                                >
                                                    {processingActions.has(user._id) ? (
                                                        <span className="material-icons">hourglass_empty</span>
                                                    ) : (
                                                        <span className="material-icons">play_circle</span>
                                                    )}
                                                    <span className="btn-label">Trial</span>
                                                </button>
                                                <button
                                                    className="action-btn-labeled upgrade-btn"
                                                    onClick={() => changeUserRole(user._id, 'USER')}
                                                    disabled={processingActions.has(user._id)}
                                                    title="Upgrade to Pro"
                                                >
                                                    {processingActions.has(user._id) ? (
                                                        <span className="material-icons">hourglass_empty</span>
                                                    ) : (
                                                        <span className="material-icons">star</span>
                                                    )}
                                                    <span className="btn-label">Pro</span>
                                                </button>
                                            </>
                                        )}
                                        
                                        {user.role === 'TRIAL' && (
                                            <button
                                                className="action-btn-labeled upgrade-btn"
                                                onClick={() => changeUserRole(user._id, 'USER')}
                                                disabled={processingActions.has(user._id)}
                                                title="Upgrade to Pro"
                                            >
                                                {processingActions.has(user._id) ? (
                                                    <span className="material-icons">hourglass_empty</span>
                                                ) : (
                                                    <span className="material-icons">star</span>
                                                )}
                                                <span className="btn-label">Pro</span>
                                            </button>
                                        )}
                                        
                                        {(user.role === 'USER' || user.role === 'TRIAL') && (
                                            <button
                                                className="action-btn-labeled downgrade-btn"
                                                onClick={() => changeUserRole(user._id, 'FREE')}
                                                disabled={processingActions.has(user._id)}
                                                title="Downgrade to Free"
                                            >
                                                {processingActions.has(user._id) ? (
                                                    <span className="material-icons">hourglass_empty</span>
                                                ) : (
                                                    <span className="material-icons">remove_circle</span>
                                                )}
                                                <span className="btn-label">Free</span>
                                            </button>
                                        )}
                                        
                                        {!user.emailVerified && (
                                            <button
                                                className="action-btn-labeled resend-btn"
                                                onClick={() => resendVerification(user._id)}
                                                disabled={processingActions.has(user._id)}
                                            >
                                                {processingActions.has(user._id) ? (
                                                    <span className="material-icons">hourglass_empty</span>
                                                ) : (
                                                    <span className="material-icons">email</span>
                                                )}
                                                <span className="btn-label">Resend</span>
                                            </button>
                                        )}
                                        
                                        {user.role !== 'ADMIN' && (
                                            <>
                                                {deleteConfirm === user._id ? (
                                                    <div className="delete-confirm-group">
                                                        <button
                                                            className="action-btn-labeled confirm-btn"
                                                            onClick={() => deleteUser(user._id)}
                                                            disabled={processingActions.has(user._id)}
                                                        >
                                                            <span className="material-icons">check</span>
                                                            <span className="btn-label">{t('buttons.confirm')}</span>
                                                        </button>
                                                        <button
                                                            className="action-btn-labeled cancel-btn"
                                                            onClick={() => setDeleteConfirm(null)}
                                                        >
                                                            <span className="material-icons">close</span>
                                                            <span className="btn-label">{t('buttons.cancel')}</span>
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        className="action-btn-labeled delete-btn"
                                                        onClick={() => setDeleteConfirm(user._id)}
                                                    >
                                                        <span className="material-icons">delete</span>
                                                        <span className="btn-label">{t('buttons.delete')}</span>
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="pagination">
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1 || loading}
                        className="pagination-btn"
                    >
                        {t('pagination.previous')}
                    </button>
                    
                    <span className="pagination-info">
                        {t('pagination.page')} {currentPage} {t('pagination.of')} {totalPages}
                    </span>
                    
                    <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages || loading}
                        className="pagination-btn"
                    >
                        {t('pagination.next')}
                    </button>
                </div>
            )}

            {/* Actions Help Popup */}
            {showActionsHelp && (
                <div className="actions-help-popup" onClick={() => setShowActionsHelp(false)}>
                    <div className="actions-help-content" onClick={(e) => e.stopPropagation()}>
                        <div className="actions-help-header">
                            <h3 className="actions-help-title">{t('userManagement.actionsHelp.title')}</h3>
                            <button
                                type="button"
                                className="actions-help-close"
                                onClick={() => setShowActionsHelp(false)}
                            >
                                <span className="material-icons">close</span>
                            </button>
                        </div>
                        <div className="actions-help-body">
                            <div className="action-help-item">
                                <span className="material-icons action-help-icon">upgrade</span>
                                <div className="action-help-content">
                                    <div className="action-help-name">{t('userManagement.actionsHelp.upgrade.name')}</div>
                                    <div className="action-help-description">{t('userManagement.actionsHelp.upgrade.description')}</div>
                                    <div className="action-help-availability">{t('userManagement.actionsHelp.upgrade.availability')}</div>
                                </div>
                            </div>
                            <div className="action-help-item">
                                <span className="material-icons action-help-icon">email</span>
                                <div className="action-help-content">
                                    <div className="action-help-name">{t('userManagement.actionsHelp.resend.name')}</div>
                                    <div className="action-help-description">{t('userManagement.actionsHelp.resend.description')}</div>
                                    <div className="action-help-availability">{t('userManagement.actionsHelp.resend.availability')}</div>
                                </div>
                            </div>
                            <div className="action-help-item">
                                <span className="material-icons action-help-icon">delete</span>
                                <div className="action-help-content">
                                    <div className="action-help-name">{t('userManagement.actionsHelp.delete.name')}</div>
                                    <div className="action-help-description">{t('userManagement.actionsHelp.delete.description')}</div>
                                    <div className="action-help-availability">{t('userManagement.actionsHelp.delete.availability')}</div>
                                </div>
                            </div>
                            <div className="action-help-item">
                                <span className="material-icons action-help-icon">check</span>
                                <div className="action-help-content">
                                    <div className="action-help-name">{t('userManagement.actionsHelp.confirm.name')}</div>
                                    <div className="action-help-description">{t('userManagement.actionsHelp.confirm.description')}</div>
                                    <div className="action-help-availability">{t('userManagement.actionsHelp.confirm.availability')}</div>
                                </div>
                            </div>
                            <div className="action-help-item">
                                <span className="material-icons action-help-icon">close</span>
                                <div className="action-help-content">
                                    <div className="action-help-name">{t('userManagement.actionsHelp.cancel.name')}</div>
                                    <div className="action-help-description">{t('userManagement.actionsHelp.cancel.description')}</div>
                                    <div className="action-help-availability">{t('userManagement.actionsHelp.cancel.availability')}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default UserManagementView; 